import { useEffect, useMemo, useState } from "react";
import {
  addDoc,
  collection,
  doc,
  onSnapshot,
  query,
  serverTimestamp,
  updateDoc,
  where,
  writeBatch
} from "firebase/firestore";
import Chart from "../components/Chart";
import ProductShell from "../components/ProductShell";
import { useAuth } from "../context/AuthContext";
import { db } from "../firebase";
import { getPlanDefinition } from "../plans";
import { formatPublicHandle, getPublicUsername } from "../utils/identity";

const origin = typeof window !== "undefined" ? window.location.origin : "";

const currencyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 2
});

const decimalFormatter = new Intl.NumberFormat("en-US", {
  maximumFractionDigits: 2
});

const MILESTONE_DEFINITIONS = {
  revenue: [100, 500, 1000, 5000],
  conversions: [1, 10, 50, 100],
  clicks: [100, 1000, 5000, 10000]
};

const PROGRAM_STAGE_OPTIONS = [
  { id: "live", label: "Live" },
  { id: "testing", label: "Testing" },
  { id: "paused", label: "Paused" }
];

const PLATFORM_OPTIONS = [
  { id: "tiktok", label: "TikTok" },
  { id: "youtube", label: "YouTube" },
  { id: "instagram", label: "Instagram" },
  { id: "blog", label: "Blog" },
  { id: "x", label: "X" },
  { id: "email", label: "Email" },
  { id: "other", label: "Other" }
];

const SALE_STATUS_OPTIONS = [
  { id: "confirmed", label: "Confirmed" },
  { id: "pending", label: "Pending" },
  { id: "reversed", label: "Reversed" }
];

function getTodaySnapshotKey() {
  const today = new Date();
  return [
    today.getFullYear(),
    String(today.getMonth() + 1).padStart(2, "0"),
    String(today.getDate()).padStart(2, "0")
  ].join("-");
}

function getOffsetSnapshotKey(daysAgo) {
  const date = new Date();
  date.setDate(date.getDate() - daysAgo);
  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, "0"),
    String(date.getDate()).padStart(2, "0")
  ].join("-");
}

function normalizeWeeklySnapshots(snapshots) {
  return snapshots && typeof snapshots === "object" && !Array.isArray(snapshots)
    ? snapshots
    : {};
}

function getNearestWeeklyBaseline(snapshots, daysAgo = 7) {
  const normalizedSnapshots = normalizeWeeklySnapshots(snapshots);
  const targetKey = getOffsetSnapshotKey(daysAgo);
  const snapshotKeys = Object.keys(normalizedSnapshots)
    .filter((key) => key <= targetKey)
    .sort();

  if (!snapshotKeys.length) {
    return null;
  }

  return normalizedSnapshots[snapshotKeys[snapshotKeys.length - 1]] || null;
}

function buildWeeklyGrowth(current, snapshots) {
  const baseline = getNearestWeeklyBaseline(snapshots);

  if (!baseline) {
    return {
      hasBaseline: false,
      clicksDelta: 0,
      conversionsDelta: 0,
      revenueDelta: 0,
      profitDelta: 0
    };
  }

  return {
    hasBaseline: true,
    clicksDelta: current.clicks - Number(baseline.clicks || 0),
    conversionsDelta: current.conversions - Number(baseline.conversions || 0),
    revenueDelta: current.revenue - Number(baseline.revenue || 0),
    profitDelta: current.profit - Number(baseline.profit || 0)
  };
}

function getProgramStageLabel(stage) {
  return PROGRAM_STAGE_OPTIONS.find((option) => option.id === stage)?.label || "Live";
}

function getPlatformLabel(platform) {
  return PLATFORM_OPTIONS.find((option) => option.id === platform)?.label || "Other";
}

function getContentLabel(link, fallback = "Untitled content") {
  return link.contentTitle || link.name || fallback;
}

function getDisplayName(member, fallback) {
  return formatPublicHandle(member, fallback);
}

function getLocalDateTimeInputValue(date = new Date()) {
  const localDate = new Date(date.getTime() - (date.getTimezoneOffset() * 60000));
  return localDate.toISOString().slice(0, 16);
}

function buildSalesByLink(sales) {
  return sales.reduce((accumulator, sale) => {
    if (!sale.linkId) {
      return accumulator;
    }

    if (!accumulator[sale.linkId]) {
      accumulator[sale.linkId] = {
        confirmedCount: 0,
        pendingCount: 0,
        reversedCount: 0,
        confirmedRevenue: 0,
        confirmedOrderValue: 0,
        recentSales: []
      };
    }

    const entry = accumulator[sale.linkId];
    const commissionAmount = Number(sale.commissionAmount || 0);
    const orderValue = Number(sale.orderValue || 0);
    const status = sale.status || "confirmed";

    if (status === "confirmed") {
      entry.confirmedCount += 1;
      entry.confirmedRevenue += commissionAmount;
      entry.confirmedOrderValue += orderValue;
    } else if (status === "pending") {
      entry.pendingCount += 1;
    } else if (status === "reversed") {
      entry.reversedCount += 1;
    }

    entry.recentSales.push(sale);
    entry.recentSales.sort((left, right) => {
      const leftTime = left.convertedAt?.seconds || 0;
      const rightTime = right.convertedAt?.seconds || 0;
      return rightTime - leftTime;
    });
    entry.recentSales = entry.recentSales.slice(0, 5);

    return accumulator;
  }, {});
}

function getLinkPerformance(link, salesByLink) {
  const saleEntry = salesByLink[link.id];

  if (saleEntry) {
    return {
      hasSales: true,
      clicks: Number(link.clicks || 0),
      conversions: saleEntry.confirmedCount,
      revenue: saleEntry.confirmedRevenue,
      orderValue: saleEntry.confirmedOrderValue,
      pendingCount: saleEntry.pendingCount,
      reversedCount: saleEntry.reversedCount,
      recentSales: saleEntry.recentSales
    };
  }

  const conversions = Number(link.conversions || 0);
  const payout = Number(link.payoutPerConversion || 0);

  return {
    hasSales: false,
    clicks: Number(link.clicks || 0),
    conversions,
    revenue: conversions * payout,
    orderValue: 0,
    pendingCount: 0,
    reversedCount: 0,
    recentSales: []
  };
}

function calculateTotals(items, salesByLink = {}) {
  return items.reduce(
    (accumulator, link) => {
      const performance = getLinkPerformance(link, salesByLink);

      return {
        clicks: accumulator.clicks + performance.clicks,
        conversions: accumulator.conversions + performance.conversions,
        revenue: accumulator.revenue + performance.revenue
      };
    },
    { clicks: 0, conversions: 0, revenue: 0 }
  );
}

function buildContentPerformance(links, salesByLink = {}) {
  return links
    .filter((link) => link.status !== "archived")
    .map((link) => {
      const performance = getLinkPerformance(link, salesByLink);
      const epc = performance.clicks ? performance.revenue / performance.clicks : 0;

      return {
        id: link.id,
        link,
        title: getContentLabel(link),
        platform: link.platform || "other",
        platformLabel: getPlatformLabel(link.platform || "other"),
        campaign: link.campaign || "",
        source: link.source || "",
        clicks: performance.clicks,
        conversions: performance.conversions,
        revenue: performance.revenue,
        epc,
        profit: performance.revenue
      };
    })
    .sort((left, right) => right.revenue - left.revenue);
}

function buildPlatformPerformance(links, salesByLink = {}) {
  const platformMap = links
    .filter((link) => link.status !== "archived")
    .reduce((accumulator, link) => {
      const platform = link.platform || "other";
      const performance = getLinkPerformance(link, salesByLink);

      if (!accumulator[platform]) {
        accumulator[platform] = {
          id: platform,
          label: getPlatformLabel(platform),
          clicks: 0,
          conversions: 0,
          revenue: 0,
          count: 0
        };
      }

      accumulator[platform].clicks += performance.clicks;
      accumulator[platform].conversions += performance.conversions;
      accumulator[platform].revenue += performance.revenue;
      accumulator[platform].count += 1;

      return accumulator;
    }, {});

  return Object.values(platformMap)
    .map((platform) => ({
      ...platform,
      epc: platform.clicks ? platform.revenue / platform.clicks : 0
    }))
    .sort((left, right) => right.revenue - left.revenue);
}

function getSaleStatusLabel(status) {
  return SALE_STATUS_OPTIONS.find((option) => option.id === status)?.label || "Confirmed";
}

function buildMilestone(value, thresholds, labelFormatter) {
  const completed = thresholds.filter((threshold) => value >= threshold);
  const nextTarget = thresholds.find((threshold) => value < threshold) || null;
  const previousTarget = completed[completed.length - 1] || 0;
  const progressSpan = nextTarget ? nextTarget - previousTarget : 1;
  const rawProgress = nextTarget ? ((value - previousTarget) / progressSpan) * 100 : 100;

  return {
    completed,
    nextTarget,
    currentLabel: labelFormatter(value),
    nextLabel: nextTarget ? labelFormatter(nextTarget) : "Final milestone reached",
    progressPercent: Math.max(0, Math.min(rawProgress, 100))
  };
}

function getDefaultGoals() {
  return {
    revenue: 1000,
    conversions: 50,
    clicks: 1000
  };
}

function normalizeGoalValues(goals) {
  const defaults = getDefaultGoals();

  return {
    revenue: Number(goals?.revenue || defaults.revenue),
    conversions: Number(goals?.conversions || defaults.conversions),
    clicks: Number(goals?.clicks || defaults.clicks)
  };
}

function normalizeMilestoneHistory(history) {
  return {
    revenue: Array.isArray(history?.revenue) ? history.revenue : [],
    conversions: Array.isArray(history?.conversions) ? history.conversions : [],
    clicks: Array.isArray(history?.clicks) ? history.clicks : []
  };
}

export default function Dashboard() {
  const { user, userProfile, workspace } = useAuth();
  const [links, setLinks] = useState([]);
  const [sales, setSales] = useState([]);
  const [members, setMembers] = useState([]);
  const [analysisFilter, setAnalysisFilter] = useState("workspace");
  const [editingLinkId, setEditingLinkId] = useState("");
  const [loggingSaleLinkId, setLoggingSaleLinkId] = useState("");
  const [editingValues, setEditingValues] = useState({
    name: "",
    destinationUrl: "",
    payoutPerConversion: "",
    platform: "tiktok",
    contentTitle: "",
    contentUrl: "",
    campaign: "",
    source: "",
    stage: "live",
    notes: ""
  });
  const [saleDrafts, setSaleDrafts] = useState({});
  const [name, setName] = useState("");
  const [destinationUrl, setDestinationUrl] = useState("");
  const [payoutPerConversion, setPayoutPerConversion] = useState("");
  const [platform, setPlatform] = useState("tiktok");
  const [contentTitle, setContentTitle] = useState("");
  const [contentUrl, setContentUrl] = useState("");
  const [campaign, setCampaign] = useState("");
  const [source, setSource] = useState("");
  const [programStage, setProgramStage] = useState("live");
  const [programNotes, setProgramNotes] = useState("");
  const [adSpend, setAdSpend] = useState("");
  const [softwareCost, setSoftwareCost] = useState("");
  const [otherCost, setOtherCost] = useState("");
  const [saving, setSaving] = useState(false);
  const [savingExpenses, setSavingExpenses] = useState(false);
  const [savingGoals, setSavingGoals] = useState(false);
  const [savingLinkId, setSavingLinkId] = useState("");
  const [savingSaleLinkId, setSavingSaleLinkId] = useState("");
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");
  const [goalForm, setGoalForm] = useState({
    revenue: "",
    conversions: "",
    clicks: ""
  });
  const [celebrationItems, setCelebrationItems] = useState([]);
  const [isCompactDashboard, setIsCompactDashboard] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    const mediaQuery = window.matchMedia("(max-width: 720px)");
    const syncCompactMode = () => setIsCompactDashboard(mediaQuery.matches);

    syncCompactMode();
    mediaQuery.addEventListener("change", syncCompactMode);

    return () => {
      mediaQuery.removeEventListener("change", syncCompactMode);
    };
  }, []);

  useEffect(() => {
    if (!userProfile?.workspaceId) {
      return undefined;
    }

    const linksQuery = query(
      collection(db, "links"),
      where("workspaceId", "==", userProfile.workspaceId)
    );
    const salesQuery = query(
      collection(db, "sales"),
      where("workspaceId", "==", userProfile.workspaceId)
    );

    const membersQuery = collection(db, "workspaces", userProfile.workspaceId, "members");

    const unsubscribeLinks = onSnapshot(
      linksQuery,
      (snapshot) => {
        setLinks(
          snapshot.docs
            .map((item) => ({
              id: item.id,
              ...item.data()
            }))
            .sort((left, right) => {
              const leftSeconds = left.createdAt?.seconds || 0;
              const rightSeconds = right.createdAt?.seconds || 0;
              return rightSeconds - leftSeconds;
            })
        );
      },
      (snapshotError) => {
        setError(snapshotError.message);
      }
    );

    const unsubscribeMembers = onSnapshot(
      membersQuery,
      (snapshot) => {
        setMembers(snapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
      },
      (snapshotError) => {
        setError(snapshotError.message);
      }
    );

    const unsubscribeSales = onSnapshot(
      salesQuery,
      (snapshot) => {
        setSales(
          snapshot.docs
            .map((item) => ({
              id: item.id,
              ...item.data()
            }))
            .sort((left, right) => {
              const leftSeconds = left.convertedAt?.seconds || left.createdAt?.seconds || 0;
              const rightSeconds = right.convertedAt?.seconds || right.createdAt?.seconds || 0;
              return rightSeconds - leftSeconds;
            })
        );
      },
      (snapshotError) => {
        setError(snapshotError.message);
      }
    );

    return () => {
      unsubscribeLinks();
      unsubscribeMembers();
      unsubscribeSales();
    };
  }, [userProfile?.workspaceId]);

  useEffect(() => {
    setAdSpend(String(workspace?.adSpend ?? ""));
    setSoftwareCost(String(workspace?.softwareCost ?? ""));
    setOtherCost(String(workspace?.otherCost ?? ""));
  }, [workspace?.adSpend, workspace?.softwareCost, workspace?.otherCost]);

  const workspaceGoals = useMemo(
    () => normalizeGoalValues(workspace?.goals),
    [workspace?.goals]
  );
  const workspaceMilestoneHistory = useMemo(
    () => normalizeMilestoneHistory(workspace?.milestoneHistory),
    [workspace?.milestoneHistory]
  );

  const activePlan = getPlanDefinition(workspace?.planId || userProfile?.planId);
  const hasAdvancedAnalytics = Boolean(activePlan.hasAdvancedAnalytics);
  const programLimit = workspace?.programLimit || userProfile?.programLimit || activePlan.programLimit;
  const seatLimit = workspace?.seatLimit || userProfile?.seatLimit || activePlan.userLimit;
  const isWorkspaceAdmin = ["owner", "admin"].includes(userProfile?.workspaceRole);
  const isTeamWorkspace = (workspace?.planId || userProfile?.planId) === "team";
  const activeLinks = useMemo(
    () => links.filter((link) => link.status !== "archived"),
    [links]
  );
  const remainingPrograms = Math.max(programLimit - activeLinks.length, 0);
  const hasReachedLimit = activeLinks.length >= programLimit;

  const activeMembers = useMemo(
    () => members.filter((member) => member.status === "active"),
    [members]
  );

  const memberDirectory = useMemo(() => {
    return activeMembers.reduce((accumulator, member) => {
      accumulator[member.uid || member.id] = member;
      return accumulator;
    }, {});
  }, [activeMembers]);
  const activeMembersByUid = useMemo(() => {
    return activeMembers.reduce((accumulator, member) => {
      accumulator[member.uid || member.id] = member;
      return accumulator;
    }, {});
  }, [activeMembers]);
  const salesByLink = useMemo(() => buildSalesByLink(sales), [sales]);

  const workspaceTotals = useMemo(() => calculateTotals(links, salesByLink), [links, salesByLink]);
  const workspaceWeeklySnapshots = useMemo(
    () => normalizeWeeklySnapshots(workspace?.weeklySnapshots),
    [workspace?.weeklySnapshots]
  );

  const totalExpenses = useMemo(() => {
    return Number(workspace?.adSpend || 0) + Number(workspace?.softwareCost || 0) + Number(workspace?.otherCost || 0);
  }, [workspace?.adSpend, workspace?.otherCost, workspace?.softwareCost]);

  const memberMetrics = useMemo(() => {
    return activeMembers.map((member) => {
      const memberLinks = links.filter((link) => link.ownerId === (member.uid || member.id));
      const totals = calculateTotals(memberLinks, salesByLink);
      const expenseAllocation = workspaceTotals.revenue > 0 ? totalExpenses * (totals.revenue / workspaceTotals.revenue) : 0;
      const estimatedProfit = totals.revenue - expenseAllocation;
      const conversionRate = totals.clicks ? (totals.conversions / totals.clicks) * 100 : 0;

      return {
        uid: member.uid || member.id,
        label: getDisplayName(member),
        email: member.email || "workspace member",
        username: getPublicUsername(member),
        role: member.role || "member",
        links: memberLinks.length,
        clicks: totals.clicks,
        conversions: totals.conversions,
        revenue: totals.revenue,
        allocatedExpenses: expenseAllocation,
        estimatedProfit,
        conversionRate
      };
    }).sort((left, right) => right.estimatedProfit - left.estimatedProfit);
  }, [activeMembers, links, salesByLink, totalExpenses, workspaceTotals.revenue]);
  const memberMetricsByUid = useMemo(() => {
    return memberMetrics.reduce((accumulator, member) => {
      accumulator[member.uid] = member;
      return accumulator;
    }, {});
  }, [memberMetrics]);

  const filterOptions = useMemo(() => {
    return [
      { id: "workspace", label: "Workspace Total" },
      ...memberMetrics.map((member) => ({
        id: member.uid,
        label: member.label
      }))
    ];
  }, [memberMetrics]);

  useEffect(() => {
    const filterStillExists = filterOptions.some((option) => option.id === analysisFilter);

    if (!filterStillExists) {
      setAnalysisFilter("workspace");
    }
  }, [analysisFilter, filterOptions]);

  const selectedMemberMetric = useMemo(
    () => memberMetrics.find((member) => member.uid === analysisFilter) || null,
    [analysisFilter, memberMetrics]
  );

  const selectedMemberRecord = useMemo(
    () => activeMembers.find((member) => (member.uid || member.id) === analysisFilter) || null,
    [activeMembers, analysisFilter]
  );

  const filteredLinks = useMemo(() => {
    if (analysisFilter === "workspace") {
      return links;
    }

    return links.filter((link) => link.ownerId === analysisFilter);
  }, [analysisFilter, links]);
  const activeFilteredLinks = useMemo(
    () => filteredLinks.filter((link) => link.status !== "archived"),
    [filteredLinks]
  );
  const archivedFilteredLinks = useMemo(
    () => filteredLinks.filter((link) => link.status === "archived"),
    [filteredLinks]
  );

  const filteredTotals = useMemo(() => calculateTotals(filteredLinks, salesByLink), [filteredLinks, salesByLink]);
  const filteredExpenses = analysisFilter === "workspace"
    ? totalExpenses
    : selectedMemberMetric?.allocatedExpenses || 0;
  const filteredNetProfit = filteredTotals.revenue - filteredExpenses;
  const conversionRate = filteredTotals.clicks ? (filteredTotals.conversions / filteredTotals.clicks) * 100 : 0;
  const earningsPerClick = filteredTotals.clicks ? filteredTotals.revenue / filteredTotals.clicks : 0;
  const profitMargin = filteredTotals.revenue ? (filteredNetProfit / filteredTotals.revenue) * 100 : 0;
  const topContent = useMemo(
    () => buildContentPerformance(filteredLinks, salesByLink),
    [filteredLinks, salesByLink]
  );
  const topPlatforms = useMemo(
    () => buildPlatformPerformance(filteredLinks, salesByLink),
    [filteredLinks, salesByLink]
  );
  const bestContent = topContent[0] || null;
  const bestPlatform = topPlatforms[0] || null;
  const recentFilteredLinks = useMemo(() => filteredLinks.slice(0, 7).reverse(), [filteredLinks]);

  const performanceLabels = useMemo(
    () => recentFilteredLinks.map((link, index) => getContentLabel(link, `Content ${index + 1}`)),
    [recentFilteredLinks]
  );

  const performanceDatasets = useMemo(() => ([
    {
      label: "Clicks",
      data: recentFilteredLinks.map((link) => getLinkPerformance(link, salesByLink).clicks),
      borderColor: "#0f2742",
      backgroundColor: "rgba(15, 39, 66, 0.08)",
      tension: 0.35,
      fill: true,
      pointBackgroundColor: "#0f2742"
    },
    {
      label: "Conversions",
      data: recentFilteredLinks.map((link) => getLinkPerformance(link, salesByLink).conversions),
      borderColor: "#bd8230",
      backgroundColor: "rgba(189, 130, 48, 0.08)",
      tension: 0.35,
      fill: true,
      pointBackgroundColor: "#bd8230"
    }
  ]), [recentFilteredLinks, salesByLink]);

  const financialDatasets = useMemo(() => {
    return [
      {
        label: "Estimated Revenue",
        data: recentFilteredLinks.map((link) => getLinkPerformance(link, salesByLink).revenue),
        borderColor: "#bd8230",
        backgroundColor: "rgba(229, 190, 109, 0.18)",
        tension: 0.35,
        fill: true,
        pointBackgroundColor: "#bd8230"
      },
      {
        label: "Allocated Expenses",
        data: recentFilteredLinks.map((link) => {
          const revenue = getLinkPerformance(link, salesByLink).revenue;
          const allocationShare = filteredTotals.revenue > 0 ? revenue / filteredTotals.revenue : 0;
          return filteredExpenses * allocationShare;
        }),
        borderColor: "#7d8794",
        backgroundColor: "rgba(125, 135, 148, 0.12)",
        tension: 0.35,
        fill: true,
        pointBackgroundColor: "#7d8794"
      },
      {
        label: "Estimated Profit",
        data: recentFilteredLinks.map((link) => {
          const revenue = getLinkPerformance(link, salesByLink).revenue;
          const allocationShare = filteredTotals.revenue > 0 ? revenue / filteredTotals.revenue : 0;
          return revenue - (filteredExpenses * allocationShare);
        }),
        borderColor: "#1f7a63",
        backgroundColor: "rgba(31, 122, 99, 0.12)",
        tension: 0.35,
        fill: true,
        pointBackgroundColor: "#1f7a63"
      }
    ];
  }, [filteredExpenses, filteredTotals.revenue, recentFilteredLinks, salesByLink]);

  const leaderboardLabels = useMemo(
    () => memberMetrics.map((member) => member.label),
    [memberMetrics]
  );

  const leaderboardTrafficDatasets = useMemo(() => ([
    {
      label: "Clicks",
      data: memberMetrics.map((member) => member.clicks),
      backgroundColor: "rgba(15, 39, 66, 0.88)",
      borderRadius: 10
    },
    {
      label: "Conversions",
      data: memberMetrics.map((member) => member.conversions),
      backgroundColor: "rgba(189, 130, 48, 0.84)",
      borderRadius: 10
    }
  ]), [memberMetrics]);

  const leaderboardFinancialDatasets = useMemo(() => ([
    {
      label: "Estimated Revenue",
      data: memberMetrics.map((member) => member.revenue),
      backgroundColor: "rgba(229, 190, 109, 0.9)",
      borderRadius: 10
    },
    {
      label: "Estimated Profit",
      data: memberMetrics.map((member) => member.estimatedProfit),
      backgroundColor: "rgba(31, 122, 99, 0.86)",
      borderRadius: 10
    }
  ]), [memberMetrics]);

  const selectedLabel = analysisFilter === "workspace"
    ? "workspace"
    : `${selectedMemberMetric?.label || "member"} results`;

  const selectedGoals = useMemo(() => {
    if (analysisFilter === "workspace") {
      return workspaceGoals;
    }

    return normalizeGoalValues(selectedMemberRecord?.goals);
  }, [analysisFilter, selectedMemberRecord?.goals, workspaceGoals]);

  const selectedMilestoneHistory = useMemo(() => {
    if (analysisFilter === "workspace") {
      return workspaceMilestoneHistory;
    }

    return normalizeMilestoneHistory(selectedMemberRecord?.milestoneHistory);
  }, [analysisFilter, selectedMemberRecord?.milestoneHistory, workspaceMilestoneHistory]);

  const canEditSelectedGoals = analysisFilter === "workspace"
    ? isWorkspaceAdmin
    : isWorkspaceAdmin || analysisFilter === user?.uid;

  useEffect(() => {
    setGoalForm({
      revenue: String(selectedGoals.revenue ?? ""),
      conversions: String(selectedGoals.conversions ?? ""),
      clicks: String(selectedGoals.clicks ?? "")
    });
  }, [selectedGoals.clicks, selectedGoals.conversions, selectedGoals.revenue]);

  const milestoneSummary = useMemo(() => {
    return {
      revenue: buildMilestone(filteredTotals.revenue, MILESTONE_DEFINITIONS.revenue, (value) => currencyFormatter.format(value)),
      conversions: buildMilestone(filteredTotals.conversions, MILESTONE_DEFINITIONS.conversions, (value) => `${value} conversions`),
      clicks: buildMilestone(filteredTotals.clicks, MILESTONE_DEFINITIONS.clicks, (value) => `${value.toLocaleString("en-US")} clicks`)
    };
  }, [filteredTotals.clicks, filteredTotals.conversions, filteredTotals.revenue]);

  const goalSummary = useMemo(() => {
    return {
      revenue: {
        current: filteredTotals.revenue,
        target: selectedGoals.revenue,
        progressPercent: selectedGoals.revenue ? Math.min((filteredTotals.revenue / selectedGoals.revenue) * 100, 100) : 0,
        currentLabel: currencyFormatter.format(filteredTotals.revenue),
        targetLabel: currencyFormatter.format(selectedGoals.revenue)
      },
      conversions: {
        current: filteredTotals.conversions,
        target: selectedGoals.conversions,
        progressPercent: selectedGoals.conversions ? Math.min((filteredTotals.conversions / selectedGoals.conversions) * 100, 100) : 0,
        currentLabel: `${filteredTotals.conversions} conversions`,
        targetLabel: `${selectedGoals.conversions} conversions`
      },
      clicks: {
        current: filteredTotals.clicks,
        target: selectedGoals.clicks,
        progressPercent: selectedGoals.clicks ? Math.min((filteredTotals.clicks / selectedGoals.clicks) * 100, 100) : 0,
        currentLabel: `${filteredTotals.clicks.toLocaleString("en-US")} clicks`,
        targetLabel: `${selectedGoals.clicks.toLocaleString("en-US")} clicks`
      }
    };
  }, [filteredTotals.clicks, filteredTotals.conversions, filteredTotals.revenue, selectedGoals.clicks, selectedGoals.conversions, selectedGoals.revenue]);
  const weeklyGrowth = useMemo(() => {
    return buildWeeklyGrowth(
      {
        clicks: filteredTotals.clicks,
        conversions: filteredTotals.conversions,
        revenue: filteredTotals.revenue,
        profit: filteredNetProfit
      },
      analysisFilter === "workspace"
        ? workspaceWeeklySnapshots
        : normalizeWeeklySnapshots(selectedMemberRecord?.weeklySnapshots)
    );
  }, [
    analysisFilter,
    filteredNetProfit,
    filteredTotals.clicks,
    filteredTotals.conversions,
    filteredTotals.revenue,
    selectedMemberRecord?.weeklySnapshots,
    workspaceWeeklySnapshots
  ]);
  const weeklyImprovementLeaders = useMemo(() => {
    return memberMetrics.map((member) => {
      const memberRecord = activeMembersByUid[member.uid];
      const growth = buildWeeklyGrowth(
        {
          clicks: member.clicks,
          conversions: member.conversions,
          revenue: member.revenue,
          profit: member.estimatedProfit
        },
        memberRecord?.weeklySnapshots
      );

      return {
        ...member,
        ...growth
      };
    }).sort((left, right) => {
      if (right.profitDelta !== left.profitDelta) {
        return right.profitDelta - left.profitDelta;
      }

      if (right.conversionsDelta !== left.conversionsDelta) {
        return right.conversionsDelta - left.conversionsDelta;
      }

      return right.clicksDelta - left.clicksDelta;
    });
  }, [activeMembersByUid, memberMetrics]);
  const desktopOpenDetails = isCompactDashboard ? {} : { open: true };

  useEffect(() => {
    const newCelebrations = [];

    Object.entries(MILESTONE_DEFINITIONS).forEach(([key, thresholds]) => {
      const currentValue = filteredTotals[key];
      const savedHistory = selectedMilestoneHistory[key] || [];
      const reached = thresholds.filter((threshold) => currentValue >= threshold && !savedHistory.includes(threshold));

      reached.forEach((threshold) => {
        newCelebrations.push({
          key: `${analysisFilter}-${key}-${threshold}`,
          title: analysisFilter === "workspace"
            ? `Workspace milestone reached`
            : `${selectedMemberMetric?.label || "Member"} milestone reached`,
          text: `${key === "revenue" ? currencyFormatter.format(threshold) : threshold.toLocaleString("en-US")} ${key} milestone unlocked.`
        });
      });
    });

    setCelebrationItems(newCelebrations);
  }, [analysisFilter, filteredTotals, selectedMemberMetric?.label, selectedMilestoneHistory]);

  useEffect(() => {
    if (!userProfile?.workspaceId) {
      return undefined;
    }

    const todayKey = getTodaySnapshotKey();
    const workspaceNeedsSnapshot = !workspaceWeeklySnapshots[todayKey];
    const membersNeedingSnapshots = activeMembers.filter((member) => {
      const memberSnapshots = normalizeWeeklySnapshots(member.weeklySnapshots);
      return !memberSnapshots[todayKey];
    });

    if (!workspaceNeedsSnapshot && !membersNeedingSnapshots.length) {
      return undefined;
    }

    const persistWeeklySnapshots = async () => {
      const batch = writeBatch(db);

      if (workspaceNeedsSnapshot) {
        batch.set(doc(db, "workspaces", userProfile.workspaceId), {
          weeklySnapshots: {
            ...workspaceWeeklySnapshots,
            [todayKey]: {
              clicks: workspaceTotals.clicks,
              conversions: workspaceTotals.conversions,
              revenue: workspaceTotals.revenue,
              profit: workspaceTotals.revenue - totalExpenses
            }
          },
          updatedAt: serverTimestamp()
        }, { merge: true });
      }

      membersNeedingSnapshots.forEach((member) => {
        const memberId = member.uid || member.id;
        const metrics = memberMetricsByUid[memberId];

        if (!metrics) {
          return;
        }

        batch.set(doc(db, "workspaces", userProfile.workspaceId, "members", memberId), {
          weeklySnapshots: {
            ...normalizeWeeklySnapshots(member.weeklySnapshots),
            [todayKey]: {
              clicks: metrics.clicks,
              conversions: metrics.conversions,
              revenue: metrics.revenue,
              profit: metrics.estimatedProfit
            }
          },
          updatedAt: serverTimestamp()
        }, { merge: true });
      });

      await batch.commit();
    };

    persistWeeklySnapshots().catch(() => {
      // Keep weekly analytics visible even if passive snapshot persistence fails.
    });

    return undefined;
  }, [
    activeMembers,
    memberMetricsByUid,
    totalExpenses,
    userProfile?.workspaceId,
    workspaceTotals.clicks,
    workspaceTotals.conversions,
    workspaceTotals.revenue,
    workspaceWeeklySnapshots
  ]);

  useEffect(() => {
    if (!userProfile?.workspaceId) {
      return undefined;
    }

    const pendingHistory = {};

    Object.entries(MILESTONE_DEFINITIONS).forEach(([key, thresholds]) => {
      const currentValue = filteredTotals[key];
      const savedHistory = selectedMilestoneHistory[key] || [];
      const reached = thresholds.filter((threshold) => currentValue >= threshold && !savedHistory.includes(threshold));

      if (reached.length) {
        pendingHistory[key] = [...savedHistory, ...reached].sort((left, right) => left - right);
      }
    });

    if (!Object.keys(pendingHistory).length) {
      return undefined;
    }

    const persistHistory = async () => {
      if (analysisFilter === "workspace") {
        await updateDoc(doc(db, "workspaces", userProfile.workspaceId), {
          milestoneHistory: {
            ...selectedMilestoneHistory,
            ...pendingHistory
          },
          updatedAt: serverTimestamp()
        });
        return;
      }

      if (!selectedMemberRecord) {
        return;
      }

      await updateDoc(doc(db, "workspaces", userProfile.workspaceId, "members", selectedMemberRecord.uid || selectedMemberRecord.id), {
        milestoneHistory: {
          ...selectedMilestoneHistory,
          ...pendingHistory
        },
        updatedAt: serverTimestamp()
      });
    };

    persistHistory().catch(() => {
      // Ignore milestone persistence errors in the passive effect and leave visible calculations intact.
    });

    return undefined;
  }, [analysisFilter, filteredTotals, selectedMemberRecord, selectedMilestoneHistory, userProfile?.workspaceId]);

  const createLink = async () => {
    const trimmedName = name.trim();
    const trimmedUrl = destinationUrl.trim();
    const trimmedPayout = payoutPerConversion.trim();
    const trimmedContentTitle = contentTitle.trim();
    const trimmedContentUrl = contentUrl.trim();
    const trimmedCampaign = campaign.trim();
    const trimmedSource = source.trim();
    const trimmedNotes = programNotes.trim();

    if (!trimmedName || !trimmedUrl) {
      setError("Add a link name and destination URL before saving.");
      return;
    }

    if (hasReachedLimit) {
      setError(`Your ${activePlan.name} plan includes up to ${programLimit} content links.`);
      return;
    }

    let normalizedUrl;
    let normalizedContentUrl = "";

    try {
      normalizedUrl = new URL(trimmedUrl);
    } catch (urlError) {
      setError("Destination URL must be a valid absolute URL.");
      return;
    }

    if (trimmedContentUrl) {
      try {
        normalizedContentUrl = new URL(trimmedContentUrl).toString();
      } catch (urlError) {
        setError("Content URL must be a valid absolute URL when provided.");
        return;
      }
    }

    if (trimmedPayout && Number.isNaN(Number(trimmedPayout))) {
      setError("Payout per conversion must be a valid number.");
      return;
    }

    setSaving(true);
    setError("");
    setStatus("");

    try {
      await addDoc(collection(db, "links"), {
        name: trimmedName,
        destinationUrl: normalizedUrl.toString(),
        payoutPerConversion: trimmedPayout ? Number(trimmedPayout) : 0,
        platform,
        contentTitle: trimmedContentTitle,
        contentUrl: normalizedContentUrl,
        campaign: trimmedCampaign,
        source: trimmedSource,
        stage: programStage,
        notes: trimmedNotes,
        ownerId: user.uid,
        ownerEmail: user.email,
        ownerUsername: userProfile?.username || getPublicUsername({ email: user.email }),
        workspaceId: userProfile.workspaceId,
        workspaceName: workspace?.name || userProfile.workspaceName,
        status: "active",
        clicks: 0,
        conversions: 0,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      setStatus("Content link created.");
      setName("");
      setDestinationUrl("");
      setPayoutPerConversion("");
      setPlatform("tiktok");
      setContentTitle("");
      setContentUrl("");
      setCampaign("");
      setSource("");
      setProgramStage("live");
      setProgramNotes("");
    } catch (saveError) {
      setError(saveError.message);
    } finally {
      setSaving(false);
    }
  };

  const saveExpenses = async () => {
    if (!userProfile?.workspaceId) {
      return;
    }

    const parsedAdSpend = adSpend.trim() ? Number(adSpend) : 0;
    const parsedSoftwareCost = softwareCost.trim() ? Number(softwareCost) : 0;
    const parsedOtherCost = otherCost.trim() ? Number(otherCost) : 0;

    if ([parsedAdSpend, parsedSoftwareCost, parsedOtherCost].some((value) => Number.isNaN(value) || value < 0)) {
      setError("Expense inputs must be valid positive numbers or zero.");
      return;
    }

    setSavingExpenses(true);
    setError("");
    setStatus("");

    try {
      await updateDoc(doc(db, "workspaces", userProfile.workspaceId), {
        adSpend: parsedAdSpend,
        softwareCost: parsedSoftwareCost,
        otherCost: parsedOtherCost,
        updatedAt: serverTimestamp()
      });

      setStatus("Workspace expenses updated.");
    } catch (saveError) {
      setError(saveError.message);
    } finally {
      setSavingExpenses(false);
    }
  };

  const saveGoals = async () => {
    if (!userProfile?.workspaceId || !canEditSelectedGoals) {
      return;
    }

    const parsedGoals = {
      revenue: Number(goalForm.revenue),
      conversions: Number(goalForm.conversions),
      clicks: Number(goalForm.clicks)
    };

    if (Object.values(parsedGoals).some((value) => Number.isNaN(value) || value <= 0)) {
      setError("Goals must be positive numbers.");
      return;
    }

    setSavingGoals(true);
    setError("");
    setStatus("");

    try {
      if (analysisFilter === "workspace") {
        await updateDoc(doc(db, "workspaces", userProfile.workspaceId), {
          goals: parsedGoals,
          updatedAt: serverTimestamp()
        });
      } else if (selectedMemberRecord) {
        await updateDoc(doc(db, "workspaces", userProfile.workspaceId, "members", selectedMemberRecord.uid || selectedMemberRecord.id), {
          goals: parsedGoals,
          updatedAt: serverTimestamp()
        });
      }

      setStatus(`${analysisFilter === "workspace" ? "Workspace" : "Member"} goals updated.`);
    } catch (saveError) {
      setError(saveError.message);
    } finally {
      setSavingGoals(false);
    }
  };

  const startEditingLink = (link) => {
    setEditingLinkId(link.id);
    setEditingValues({
      name: link.name || "",
      destinationUrl: link.destinationUrl || "",
      payoutPerConversion: String(link.payoutPerConversion ?? ""),
      platform: link.platform || "tiktok",
      contentTitle: link.contentTitle || "",
      contentUrl: link.contentUrl || "",
      campaign: link.campaign || "",
      source: link.source || "",
      stage: link.stage || "live",
      notes: link.notes || ""
    });
    setError("");
    setStatus("");
  };

  const cancelEditingLink = () => {
    setEditingLinkId("");
    setEditingValues({
      name: "",
      destinationUrl: "",
      payoutPerConversion: "",
      platform: "tiktok",
      contentTitle: "",
      contentUrl: "",
      campaign: "",
      source: "",
      stage: "live",
      notes: ""
    });
  };

  const getDefaultSaleDraft = (link) => ({
    status: "confirmed",
    commissionAmount: String(Number(link.payoutPerConversion || 0) || ""),
    orderValue: "",
    network: "manual",
    externalConversionId: "",
    campaign: "",
    convertedAt: getLocalDateTimeInputValue(),
    notes: ""
  });

  const startLoggingSale = (link) => {
    setLoggingSaleLinkId(link.id);
    setSaleDrafts((current) => ({
      ...current,
      [link.id]: current[link.id] || getDefaultSaleDraft(link)
    }));
    setError("");
    setStatus("");
  };

  const cancelLoggingSale = (link) => {
    setLoggingSaleLinkId((current) => (current === link.id ? "" : current));
    setSaleDrafts((current) => {
      const next = { ...current };
      delete next[link.id];
      return next;
    });
  };

  const updateSaleDraft = (linkId, field, value) => {
    setSaleDrafts((current) => ({
      ...current,
      [linkId]: {
        ...(current[linkId] || {}),
        [field]: value
      }
    }));
  };

  const createSale = async (link) => {
    if (!userProfile?.workspaceId) {
      return;
    }

    const draft = saleDrafts[link.id] || getDefaultSaleDraft(link);
    const trimmedCommission = draft.commissionAmount.trim();
    const trimmedOrderValue = draft.orderValue.trim();
    const trimmedNetwork = draft.network.trim();
    const trimmedExternalId = draft.externalConversionId.trim();
    const trimmedCampaign = draft.campaign.trim();
    const trimmedNotes = draft.notes.trim();
    const commissionAmount = Number(trimmedCommission);
    const orderValue = trimmedOrderValue ? Number(trimmedOrderValue) : 0;
    const convertedAt = draft.convertedAt ? new Date(draft.convertedAt) : new Date();

    if (!trimmedCommission || Number.isNaN(commissionAmount) || commissionAmount < 0) {
      setError("Commission amount must be a valid number greater than or equal to zero.");
      return;
    }

    if (trimmedOrderValue && (Number.isNaN(orderValue) || orderValue < 0)) {
      setError("Order value must be a valid number greater than or equal to zero.");
      return;
    }

    if (Number.isNaN(convertedAt.getTime())) {
      setError("Converted date must be a valid date and time.");
      return;
    }

    if (trimmedExternalId) {
      const duplicateSale = sales.find((sale) => (
        sale.workspaceId === userProfile.workspaceId
        && sale.linkId === link.id
        && sale.externalConversionId === trimmedExternalId
      ));

      if (duplicateSale) {
        setError("That external conversion ID has already been logged for this content link.");
        return;
      }
    }

    setSavingSaleLinkId(link.id);
    setError("");
    setStatus("");

    try {
      const batch = writeBatch(db);
      const saleRef = doc(collection(db, "sales"));
      const linkRef = doc(db, "links", link.id);

      batch.set(saleRef, {
        workspaceId: userProfile.workspaceId,
        linkId: link.id,
        linkName: link.name || "",
        contentTitle: link.contentTitle || link.name || "",
        platform: link.platform || "other",
        ownerId: link.ownerId || user.uid,
        ownerUsername: link.ownerUsername || userProfile?.username || getPublicUsername({ email: user.email }),
        status: draft.status || "confirmed",
        network: trimmedNetwork || "manual",
        externalConversionId: trimmedExternalId || null,
        commissionAmount,
        orderValue,
        currency: "USD",
        campaign: trimmedCampaign || null,
        notes: trimmedNotes || null,
        convertedAt,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        loggedByUid: user.uid
      });

      batch.update(linkRef, {
        conversions: (Number(link.conversions || 0) + (draft.status === "confirmed" ? 1 : 0)),
        lastConvertedAt: draft.status === "confirmed" ? convertedAt : link.lastConvertedAt || null,
        updatedAt: serverTimestamp()
      });

      await batch.commit();

      setStatus("Sale logged safely without storing buyer identity.");
      cancelLoggingSale(link);
    } catch (saveError) {
      setError(saveError.message);
    } finally {
      setSavingSaleLinkId("");
    }
  };

  const saveLinkChanges = async (link) => {
    const trimmedName = editingValues.name.trim();
    const trimmedUrl = editingValues.destinationUrl.trim();
    const trimmedPayout = editingValues.payoutPerConversion.trim();
    const trimmedContentUrl = editingValues.contentUrl.trim();

    if (!trimmedName || !trimmedUrl) {
      setError("Content link name and destination URL are required.");
      return;
    }

    let normalizedUrl;
    let normalizedContentUrl = "";

    try {
      normalizedUrl = new URL(trimmedUrl);
    } catch (urlError) {
      setError("Destination URL must be a valid absolute URL.");
      return;
    }

    if (trimmedContentUrl) {
      try {
        normalizedContentUrl = new URL(trimmedContentUrl).toString();
      } catch (urlError) {
        setError("Content URL must be a valid absolute URL when provided.");
        return;
      }
    }

    if (trimmedPayout && Number.isNaN(Number(trimmedPayout))) {
      setError("Payout per conversion must be a valid number.");
      return;
    }

    setSavingLinkId(link.id);
    setError("");
    setStatus("");

    try {
      await updateDoc(doc(db, "links", link.id), {
        name: trimmedName,
        destinationUrl: normalizedUrl.toString(),
        payoutPerConversion: trimmedPayout ? Number(trimmedPayout) : 0,
        platform: editingValues.platform || "other",
        contentTitle: editingValues.contentTitle.trim(),
        contentUrl: normalizedContentUrl,
        campaign: editingValues.campaign.trim(),
        source: editingValues.source.trim(),
        stage: editingValues.stage || "live",
        notes: editingValues.notes.trim(),
        updatedAt: serverTimestamp()
      });

      setStatus("Content link updated.");
      cancelEditingLink();
    } catch (saveError) {
      setError(saveError.message);
    } finally {
      setSavingLinkId("");
    }
  };

  const toggleLinkStatus = async (link, nextStatus) => {
    setSavingLinkId(link.id);
    setError("");
    setStatus("");

    try {
      await updateDoc(doc(db, "links", link.id), {
        status: nextStatus,
        updatedAt: serverTimestamp()
      });

      if (editingLinkId === link.id && nextStatus === "archived") {
        cancelEditingLink();
      }

      setStatus(nextStatus === "archived" ? "Content link removed from active use." : "Content link restored.");
    } catch (saveError) {
      setError(saveError.message);
    } finally {
      setSavingLinkId("");
    }
  };

  const renderLinkCard = (link) => {
    const trackingUrl = `${origin}/t/${link.id}`;
    const performance = getLinkPerformance(link, salesByLink);
    const estimatedRevenue = performance.revenue;
    const expenseAllocation = filteredTotals.revenue > 0 ? filteredExpenses * (estimatedRevenue / filteredTotals.revenue) : 0;
    const estimatedProfit = estimatedRevenue - expenseAllocation;
    const isEditing = editingLinkId === link.id;
    const isLoggingSale = loggingSaleLinkId === link.id;
    const isSavingThisLink = savingLinkId === link.id;
    const isSavingThisSale = savingSaleLinkId === link.id;
    const saleDraft = saleDrafts[link.id] || getDefaultSaleDraft(link);
    const createdBy = formatPublicHandle(
      {
        username: link.ownerUsername || memberDirectory[link.ownerId]?.username,
        email: link.ownerEmail || memberDirectory[link.ownerId]?.email
      },
      link.ownerEmail || memberDirectory[link.ownerId]?.email
    );

    return (
      <article
        key={link.id}
        className={link.status === "archived" ? "link-card link-card-archived" : "link-card"}
      >
        <div className="link-card-header">
          <div>
            <div className="link-card-status-row">
              <h3>{getContentLabel(link)}</h3>
              <span className={`platform-badge platform-${link.platform || "other"}`}>
                {getPlatformLabel(link.platform || "other")}
              </span>
              <span className={link.status === "archived" ? "mobile-chip muted-chip" : "mobile-chip"}>
                {link.status === "archived" ? "Removed" : getProgramStageLabel(link.stage)}
              </span>
            </div>
            <p className="muted-text">
              {link.campaign ? `${link.campaign} | ` : ""}
              {link.source ? `${link.source} | ` : ""}
              Destination: {link.destinationUrl}
            </p>
            {link.contentUrl ? <p className="muted-text">Content URL: {link.contentUrl}</p> : null}
          </div>

          <div className="link-card-actions">
            {link.status !== "archived" ? (
              <>
                <button
                  className={isLoggingSale ? "filter-pill active" : "filter-pill"}
                  onClick={() => (isLoggingSale ? cancelLoggingSale(link) : startLoggingSale(link))}
                  type="button"
                >
                  {isLoggingSale ? "Cancel Sale" : "Log Sale"}
                </button>
                <button
                  className={isEditing ? "filter-pill active" : "filter-pill"}
                  onClick={() => (isEditing ? cancelEditingLink() : startEditingLink(link))}
                  type="button"
                >
                  {isEditing ? "Cancel Edit" : "Edit"}
                </button>
                <button
                  className="filter-pill"
                  disabled={isSavingThisLink}
                  onClick={() => toggleLinkStatus(link, "archived")}
                  type="button"
                >
                  {isSavingThisLink ? "Saving..." : "Remove Link"}
                </button>
              </>
            ) : (
              <button
                className="filter-pill"
                disabled={isSavingThisLink}
                onClick={() => toggleLinkStatus(link, "active")}
                type="button"
              >
                {isSavingThisLink ? "Saving..." : "Reactivate"}
              </button>
            )}
          </div>
        </div>

        {isEditing ? (
          <div className="inline-form-grid">
            <div>
              <label className="field-label" htmlFor={`edit-name-${link.id}`}>
                Content link name
              </label>
              <input
                id={`edit-name-${link.id}`}
                value={editingValues.name}
                onChange={(event) => setEditingValues((current) => ({ ...current, name: event.target.value }))}
              />
            </div>

            <div>
              <label className="field-label" htmlFor={`edit-destination-${link.id}`}>
                Affiliate destination URL
              </label>
              <input
                id={`edit-destination-${link.id}`}
                value={editingValues.destinationUrl}
                onChange={(event) => setEditingValues((current) => ({ ...current, destinationUrl: event.target.value }))}
              />
            </div>

            <div>
              <label className="field-label" htmlFor={`edit-payout-${link.id}`}>
                Payout per conversion
              </label>
              <input
                id={`edit-payout-${link.id}`}
                value={editingValues.payoutPerConversion}
                onChange={(event) => setEditingValues((current) => ({ ...current, payoutPerConversion: event.target.value }))}
              />
            </div>

            <div>
              <label className="field-label" htmlFor={`edit-platform-${link.id}`}>
                Platform
              </label>
              <select
                id={`edit-platform-${link.id}`}
                value={editingValues.platform}
                onChange={(event) => setEditingValues((current) => ({ ...current, platform: event.target.value }))}
              >
                {PLATFORM_OPTIONS.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="field-label" htmlFor={`edit-content-title-${link.id}`}>
                Content title
              </label>
              <input
                id={`edit-content-title-${link.id}`}
                placeholder="TikTok video, YouTube Short, blog post..."
                value={editingValues.contentTitle}
                onChange={(event) => setEditingValues((current) => ({ ...current, contentTitle: event.target.value }))}
              />
            </div>

            <div>
              <label className="field-label" htmlFor={`edit-content-url-${link.id}`}>
                Content URL
              </label>
              <input
                id={`edit-content-url-${link.id}`}
                placeholder="https://..."
                value={editingValues.contentUrl}
                onChange={(event) => setEditingValues((current) => ({ ...current, contentUrl: event.target.value }))}
              />
            </div>

            <div>
              <label className="field-label" htmlFor={`edit-campaign-${link.id}`}>
                Campaign
              </label>
              <input
                id={`edit-campaign-${link.id}`}
                placeholder="Spring launch"
                value={editingValues.campaign}
                onChange={(event) => setEditingValues((current) => ({ ...current, campaign: event.target.value }))}
              />
            </div>

            <div>
              <label className="field-label" htmlFor={`edit-source-${link.id}`}>
                Source
              </label>
              <input
                id={`edit-source-${link.id}`}
                placeholder="bio link, description, newsletter..."
                value={editingValues.source}
                onChange={(event) => setEditingValues((current) => ({ ...current, source: event.target.value }))}
              />
            </div>

            <div>
              <label className="field-label" htmlFor={`edit-stage-${link.id}`}>
                Content status
              </label>
              <select
                id={`edit-stage-${link.id}`}
                value={editingValues.stage}
                onChange={(event) => setEditingValues((current) => ({ ...current, stage: event.target.value }))}
              >
                {PROGRAM_STAGE_OPTIONS.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="inline-form-span">
              <label className="field-label" htmlFor={`edit-notes-${link.id}`}>
                Notes
              </label>
              <textarea
                id={`edit-notes-${link.id}`}
                rows="3"
                value={editingValues.notes}
                onChange={(event) => setEditingValues((current) => ({ ...current, notes: event.target.value }))}
              />
            </div>

            <div className="link-card-actions">
              <button disabled={isSavingThisLink} onClick={() => saveLinkChanges(link)} type="button">
                {isSavingThisLink ? "Saving..." : "Save Changes"}
              </button>
              <button className="filter-pill" onClick={cancelEditingLink} type="button">
                Cancel
              </button>
            </div>
          </div>
        ) : null}

        {isLoggingSale ? (
          <div className="sales-form-grid">
            <div>
              <label className="field-label" htmlFor={`sale-status-${link.id}`}>
                Sale status
              </label>
              <select
                id={`sale-status-${link.id}`}
                value={saleDraft.status}
                onChange={(event) => updateSaleDraft(link.id, "status", event.target.value)}
              >
                {SALE_STATUS_OPTIONS.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="field-label" htmlFor={`sale-commission-${link.id}`}>
                Commission amount
              </label>
              <input
                id={`sale-commission-${link.id}`}
                placeholder="19.00"
                value={saleDraft.commissionAmount}
                onChange={(event) => updateSaleDraft(link.id, "commissionAmount", event.target.value)}
              />
            </div>

            <div>
              <label className="field-label" htmlFor={`sale-order-${link.id}`}>
                Order value
              </label>
              <input
                id={`sale-order-${link.id}`}
                placeholder="97.00"
                value={saleDraft.orderValue}
                onChange={(event) => updateSaleDraft(link.id, "orderValue", event.target.value)}
              />
            </div>

            <div>
              <label className="field-label" htmlFor={`sale-network-${link.id}`}>
                Network
              </label>
              <input
                id={`sale-network-${link.id}`}
                placeholder="manual"
                value={saleDraft.network}
                onChange={(event) => updateSaleDraft(link.id, "network", event.target.value)}
              />
            </div>

            <div>
              <label className="field-label" htmlFor={`sale-converted-${link.id}`}>
                Converted at
              </label>
              <input
                id={`sale-converted-${link.id}`}
                type="datetime-local"
                value={saleDraft.convertedAt}
                onChange={(event) => updateSaleDraft(link.id, "convertedAt", event.target.value)}
              />
            </div>

            <div>
              <label className="field-label" htmlFor={`sale-external-${link.id}`}>
                External conversion ID
              </label>
              <input
                id={`sale-external-${link.id}`}
                placeholder="impact-1234"
                value={saleDraft.externalConversionId}
                onChange={(event) => updateSaleDraft(link.id, "externalConversionId", event.target.value)}
              />
            </div>

            <div>
              <label className="field-label" htmlFor={`sale-campaign-${link.id}`}>
                Campaign
              </label>
              <input
                id={`sale-campaign-${link.id}`}
                placeholder="youtube-april"
                value={saleDraft.campaign}
                onChange={(event) => updateSaleDraft(link.id, "campaign", event.target.value)}
              />
            </div>

            <div className="inline-form-span">
              <label className="field-label" htmlFor={`sale-notes-${link.id}`}>
                Internal notes
              </label>
              <textarea
                id={`sale-notes-${link.id}`}
                rows="2"
                placeholder="Optional internal notes only. Do not add customer identity."
                value={saleDraft.notes}
                onChange={(event) => updateSaleDraft(link.id, "notes", event.target.value)}
              />
            </div>

            <div className="inline-form-span sales-form-hint">
              Log only affiliate-safe metadata here, like commission amount, order value, network, and campaign. Do not store buyer names, emails, addresses, or payment details.
            </div>

            <div className="link-card-actions">
              <button disabled={isSavingThisSale} onClick={() => createSale(link)} type="button">
                {isSavingThisSale ? "Saving..." : "Save Sale"}
              </button>
              <button className="filter-pill" onClick={() => cancelLoggingSale(link)} type="button">
                Cancel
              </button>
            </div>
          </div>
        ) : null}

        <div className="link-meta">
          <span>Clicks: {link.clicks || 0}</span>
          <span>Conversions: {performance.conversions || 0}</span>
          <span>Est. revenue: {currencyFormatter.format(estimatedRevenue)}</span>
          <span>Est. profit: {currencyFormatter.format(estimatedProfit)}</span>
          <span>EPC: {currencyFormatter.format(performance.clicks ? estimatedRevenue / performance.clicks : 0)}</span>
        </div>

        <div className="sales-summary-row">
          <span className="mobile-chip">Confirmed sales: {performance.conversions}</span>
          <span className="mobile-chip muted-chip">Pending: {performance.pendingCount}</span>
          <span className="mobile-chip muted-chip">Reversed: {performance.reversedCount}</span>
          <span className="mobile-chip muted-chip">Order value: {currencyFormatter.format(performance.orderValue)}</span>
        </div>

        <p className="muted-text">
          Created by {createdBy} | Payout per conversion: {currencyFormatter.format(Number(link.payoutPerConversion || 0))}
        </p>
        {link.notes ? <p className="program-note">{link.notes}</p> : null}

        {performance.recentSales.length ? (
          <div className="sales-list">
            <span className="eyebrow">Recent Sales</span>
            {performance.recentSales.map((sale) => (
              <div className="sale-item" key={sale.id}>
                <div className="sale-item-top">
                  <strong>{currencyFormatter.format(Number(sale.commissionAmount || 0))}</strong>
                  <span className={sale.status === "confirmed" ? "mobile-chip" : "mobile-chip muted-chip"}>
                    {getSaleStatusLabel(sale.status)}
                  </span>
                </div>
                <div className="sale-meta">
                  <span>{sale.network || "manual"}</span>
                  <span>{sale.campaign || "no campaign"}</span>
                  <span>
                    {sale.convertedAt?.toDate
                      ? sale.convertedAt.toDate().toLocaleString("en-US")
                      : "time unavailable"}
                  </span>
                </div>
              </div>
            ))}
          </div>
        ) : null}

        <label className="field-label" htmlFor={`tracking-${link.id}`}>
          Tracking URL
        </label>
        <input id={`tracking-${link.id}`} readOnly value={trackingUrl} />
        {link.status === "archived" ? (
          <p className="muted-text">Removes it from active use but keeps historical data. The tracking link stops redirecting until you reactivate it.</p>
        ) : null}
      </article>
    );
  };

  return (
    <ProductShell
      action={<button onClick={createLink} type="button">{saving ? "Saving..." : "Add Content Link"}</button>}
      subtitle={`Signed in as ${userProfile?.publicHandle || formatPublicHandle({ email: user?.email })}. ${activePlan.name} includes ${programLimit} content links and ${seatLimit} user${seatLimit === 1 ? "" : "s"}.`}
      title={workspace?.name || userProfile?.workspaceName || user?.email}
    >
      <div className="dashboard-shell product-page">
        <section className="creator-hero-metrics">
          <article className="card revenue-hero-card">
            <span className="eyebrow">Creator Revenue</span>
            <strong>{currencyFormatter.format(filteredTotals.revenue)}</strong>
            <p>
              {bestContent
                ? `${bestContent.title} is leading with ${currencyFormatter.format(bestContent.revenue)}.`
                : "Create your first content link to see exactly which content makes you money."}
            </p>
            <div className="sparkline" aria-hidden="true">
              {[35, 48, 44, 61, 58, 76, 88].map((height, index) => (
                <span key={height + index} style={{ height: `${height}%` }} />
              ))}
            </div>
          </article>

          <article className="card creator-metric-card">
            <span className="eyebrow">EPC</span>
            <strong>{currencyFormatter.format(earningsPerClick)}</strong>
            <p>Revenue per click across this view.</p>
          </article>

          <article className="card creator-metric-card">
            <span className="eyebrow">Conversions</span>
            <strong>{filteredTotals.conversions}</strong>
            <p>{decimalFormatter.format(conversionRate)}% conversion rate.</p>
          </article>

          <article className="card creator-metric-card">
            <span className="eyebrow">Top Platform</span>
            <strong>{bestPlatform ? bestPlatform.label : "Waiting"}</strong>
            <p>{bestPlatform ? `${currencyFormatter.format(bestPlatform.revenue)} tracked revenue.` : "Add a platform to your first link."}</p>
          </article>
        </section>

        <section className="dashboard-grid metrics-grid">
          <details className="card form-card dashboard-details create-link-details" {...desktopOpenDetails}>
            <summary>
              <span>Track a content link</span>
              <small>{remainingPrograms} slots left</small>
            </summary>
            <div className="dashboard-details-body">
            <label className="field-label" htmlFor="link-name">
              Content link name
            </label>
            <input
              id="link-name"
              placeholder="TikTok bio link, YouTube description, newsletter..."
              value={name}
              onChange={(event) => setName(event.target.value)}
            />

            <label className="field-label" htmlFor="platform">
              Where do you create content?
            </label>
            <select
              id="platform"
              value={platform}
              onChange={(event) => setPlatform(event.target.value)}
            >
              {PLATFORM_OPTIONS.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </select>

            <label className="field-label" htmlFor="content-title">
              What do you want to track first?
            </label>
            <input
              id="content-title"
              placeholder="AI tools tutorial, camera gear review, weekly newsletter..."
              value={contentTitle}
              onChange={(event) => setContentTitle(event.target.value)}
            />

            <label className="field-label" htmlFor="content-url">
              Content URL
            </label>
            <input
              id="content-url"
              placeholder="https://www.youtube.com/shorts/..."
              value={contentUrl}
              onChange={(event) => setContentUrl(event.target.value)}
            />

            <label className="field-label" htmlFor="destination-url">
              Affiliate destination URL
            </label>
            <input
              id="destination-url"
              placeholder="https://partner.example.com/offer"
              value={destinationUrl}
              onChange={(event) => setDestinationUrl(event.target.value)}
            />

            <label className="field-label" htmlFor="payout-per-conversion">
              Payout per conversion
            </label>
            <input
              id="payout-per-conversion"
              placeholder="25.00"
              value={payoutPerConversion}
              onChange={(event) => setPayoutPerConversion(event.target.value)}
            />

            <label className="field-label" htmlFor="campaign">
              Campaign
            </label>
            <input
              id="campaign"
              placeholder="May creator push"
              value={campaign}
              onChange={(event) => setCampaign(event.target.value)}
            />

            <label className="field-label" htmlFor="source">
              Source placement
            </label>
            <input
              id="source"
              placeholder="bio link, pinned comment, description, email CTA..."
              value={source}
              onChange={(event) => setSource(event.target.value)}
            />

            <label className="field-label" htmlFor="program-stage">
              Content status
            </label>
            <select
              id="program-stage"
              value={programStage}
              onChange={(event) => setProgramStage(event.target.value)}
            >
              {PROGRAM_STAGE_OPTIONS.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </select>

            <label className="field-label" htmlFor="program-notes">
              Notes
            </label>
            <textarea
              id="program-notes"
              placeholder="Hook, angle, audience, or what you are testing."
              rows="3"
              value={programNotes}
              onChange={(event) => setProgramNotes(event.target.value)}
            />

            {error ? <p className="status error-text">{error}</p> : null}
            {status ? <p className="status success-text">{status}</p> : null}
            <p className="muted-text">
              {remainingPrograms} of {programLimit} content link slots remaining.
            </p>

            <button disabled={saving} onClick={createLink} type="button">
              {saving ? "Saving..." : "Create Content Link"}
            </button>
            </div>
          </details>

          <div className="card stats-card stat-highlight">
            <h2>{analysisFilter === "workspace" ? "Revenue snapshot" : `${selectedMemberMetric?.label || "Member"} revenue`}</h2>
            <div className="stat-row">
              <span>Content links</span>
              <strong>{activeFilteredLinks.length}</strong>
            </div>
            <div className="stat-row">
              <span>Removed links</span>
              <strong>{archivedFilteredLinks.length}</strong>
            </div>
            <div className="stat-row">
              <span>Estimated revenue</span>
              <strong>{currencyFormatter.format(filteredTotals.revenue)}</strong>
            </div>
            <div className="stat-row">
              <span>EPC</span>
              <strong>{currencyFormatter.format(earningsPerClick)}</strong>
            </div>
            <div className="stat-row">
              <span>Clicks / Conversions</span>
              <strong>{filteredTotals.clicks} / {filteredTotals.conversions}</strong>
            </div>
            <div className="plan-badge">
              Viewing {analysisFilter === "workspace" ? "workspace total" : selectedMemberMetric?.label} | Plan: {activePlan.name}
            </div>
          </div>
        </section>

        {hasAdvancedAnalytics ? (
          <section className="dashboard-grid creator-insight-grid">
            <section className="card ranking-card">
              <div className="section-heading">
                <div>
                  <span className="eyebrow">Top Content</span>
                  <h2>What is making money</h2>
                </div>
              </div>
              {topContent.length ? (
                <div className="ranking-list">
                  {topContent.slice(0, 5).map((item, index) => (
                    <article className="ranking-item" key={item.id}>
                      <span className="ranking-number">{index + 1}</span>
                      <div>
                        <h3>{item.title}</h3>
                        <p>{item.platformLabel}{item.campaign ? ` | ${item.campaign}` : ""}</p>
                      </div>
                      <div className="ranking-values">
                        <strong>{currencyFormatter.format(item.revenue)}</strong>
                        <span>{currencyFormatter.format(item.epc)} EPC</span>
                      </div>
                    </article>
                  ))}
                </div>
              ) : (
                <div className="empty-state">
                  <h3>Track your first piece of content</h3>
                  <p>Add a platform and content title to start seeing which posts, videos, or emails drive revenue.</p>
                </div>
              )}
            </section>

            <section className="card ranking-card">
              <div className="section-heading">
                <div>
                  <span className="eyebrow">Top Platform</span>
                  <h2>Where revenue comes from</h2>
                </div>
              </div>
              {topPlatforms.length ? (
                <div className="platform-list">
                  {topPlatforms.slice(0, 6).map((item) => (
                    <article className="platform-row" key={item.id}>
                      <span className={`platform-badge platform-${item.id}`}>{item.label}</span>
                      <div className="platform-bar" aria-hidden="true">
                        <span style={{ width: `${filteredTotals.revenue ? Math.max(8, (item.revenue / filteredTotals.revenue) * 100) : 8}%` }} />
                      </div>
                      <strong>{currencyFormatter.format(item.revenue)}</strong>
                      <span>{item.clicks} clicks</span>
                    </article>
                  ))}
                </div>
              ) : (
                <div className="empty-state">
                  <h3>No platform data yet</h3>
                  <p>Choose TikTok, YouTube, Instagram, Blog, X, or Email when you create a content link.</p>
                </div>
              )}
            </section>
          </section>
        ) : (
          <section className="card analytics-upgrade-card">
            <span className="eyebrow">Pro Analytics</span>
            <h2>Unlock content, platform, profit, goals, and trend analysis</h2>
            <p className="muted-text">
              Basic keeps your starter click, conversion, revenue, and EPC snapshot. Pro expands this dashboard with
              ranked content, platform comparisons, finance details, weekly growth, custom goals, milestones, and charts.
            </p>
            <a className="cta-button" href="/pricing">View Pro Upgrade</a>
          </section>
        )}

        {hasAdvancedAnalytics && isTeamWorkspace && memberMetrics.length ? (
          <>
            <section className="card">
              <div className="section-heading">
                <div>
                  <span className="eyebrow">Weekly Team Momentum</span>
                  <h2>Who improved this week</h2>
                </div>
                <p className="muted-text">A coach-friendly view of who gained the most profit, conversions, and clicks over the last saved week.</p>
              </div>
              <section className="member-performance-grid">
                {weeklyImprovementLeaders.slice(0, 3).map((member) => (
                  <article className="card member-card" key={`improver-${member.uid}`}>
                    <div className="member-card-top">
                      <div>
                        <span className="eyebrow">Weekly Improver</span>
                        <h3>{member.label}</h3>
                      </div>
                      <button
                        className={analysisFilter === member.uid ? "filter-pill active" : "filter-pill"}
                        onClick={() => setAnalysisFilter(member.uid)}
                        type="button"
                      >
                        View Results
                      </button>
                    </div>
                    <p className="muted-text">{member.hasBaseline ? "Compared with the last saved weekly snapshot" : "Weekly snapshot is still collecting"}</p>
                    <div className="member-stat-grid">
                      <span>Profit: {member.hasBaseline ? `${member.profitDelta >= 0 ? "+" : ""}${currencyFormatter.format(member.profitDelta)}` : "Pending"}</span>
                      <span>Conversions: {member.hasBaseline ? `${member.conversionsDelta >= 0 ? "+" : ""}${member.conversionsDelta}` : "Pending"}</span>
                      <span>Clicks: {member.hasBaseline ? `${member.clicksDelta >= 0 ? "+" : ""}${member.clicksDelta}` : "Pending"}</span>
                      <span>Revenue: {member.hasBaseline ? `${member.revenueDelta >= 0 ? "+" : ""}${currencyFormatter.format(member.revenueDelta)}` : "Pending"}</span>
                    </div>
                  </article>
                ))}
              </section>
            </section>

            <section className="card">
              <div className="section-heading">
                <div>
                  <span className="eyebrow">Analytics Filter</span>
                  <h2>Workspace total vs individual member results</h2>
                </div>
                <p className="muted-text">
                  Switch the whole dashboard between collected team growth and one member&apos;s contribution.
                </p>
              </div>
              <div className="filter-pill-row">
                {filterOptions.map((option) => (
                  <button
                    key={option.id}
                    className={option.id === analysisFilter ? "filter-pill active" : "filter-pill"}
                    onClick={() => setAnalysisFilter(option.id)}
                    type="button"
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </section>

            <section className="member-performance-grid">
              {memberMetrics.map((member) => (
                <article className="card member-card" key={member.uid}>
                  <div className="member-card-top">
                    <div>
                      <span className="eyebrow">Team Member</span>
                      <h3>{member.label}</h3>
                    </div>
                    <button
                      className={analysisFilter === member.uid ? "filter-pill active" : "filter-pill"}
                      onClick={() => setAnalysisFilter(member.uid)}
                      type="button"
                    >
                      View Results
                    </button>
                  </div>
                  <p className="muted-text">{member.email} | {member.role}</p>
                  <div className="member-stat-grid">
                    <span>Content links: {member.links}</span>
                    <span>Clicks: {member.clicks}</span>
                    <span>Conversions: {member.conversions}</span>
                    <span>Revenue: {currencyFormatter.format(member.revenue)}</span>
                    <span>Profit: {currencyFormatter.format(member.estimatedProfit)}</span>
                    <span>Conv. rate: {decimalFormatter.format(member.conversionRate)}%</span>
                  </div>
                </article>
              ))}
            </section>
          </>
        ) : null}

        {hasAdvancedAnalytics ? (
        <>
        <details className="dashboard-details advanced-dashboard-details" {...desktopOpenDetails}>
          <summary>
            <span>Finance details</span>
            <small>Profit, goals, expenses</small>
          </summary>
          <div className="dashboard-details-body">
        <section className="dashboard-grid finance-summary-grid">
          <article className="card finance-card">
            <span className="eyebrow">Weekly Click Growth</span>
            <strong>{weeklyGrowth.hasBaseline ? `${weeklyGrowth.clicksDelta >= 0 ? "+" : ""}${weeklyGrowth.clicksDelta.toLocaleString("en-US")}` : "Collecting data"}</strong>
            <p className="muted-text">Change in clicks compared with the last saved snapshot from at least 7 days ago.</p>
          </article>

          <article className="card finance-card">
            <span className="eyebrow">Weekly Conversion Growth</span>
            <strong>{weeklyGrowth.hasBaseline ? `${weeklyGrowth.conversionsDelta >= 0 ? "+" : ""}${weeklyGrowth.conversionsDelta}` : "Collecting data"}</strong>
            <p className="muted-text">How many more conversions this {analysisFilter === "workspace" ? "workspace" : "member"} recorded over the past week.</p>
          </article>

          <article className="card finance-card">
            <span className="eyebrow">Weekly Profit Growth</span>
            <strong className={weeklyGrowth.hasBaseline && weeklyGrowth.profitDelta < 0 ? "negative-value" : ""}>
              {weeklyGrowth.hasBaseline ? `${weeklyGrowth.profitDelta >= 0 ? "+" : ""}${currencyFormatter.format(weeklyGrowth.profitDelta)}` : "Collecting data"}
            </strong>
            <p className="muted-text">Estimated profit change since the prior weekly snapshot for the current {selectedLabel}.</p>
          </article>

          <article className="card finance-card expense-card">
            <span className="eyebrow">Monthly Expenses</span>
            <label className="field-label" htmlFor="ad-spend">
              Ad spend
            </label>
            <input
              id="ad-spend"
              disabled={!isWorkspaceAdmin}
              placeholder="250.00"
              value={adSpend}
              onChange={(event) => setAdSpend(event.target.value)}
            />

            <label className="field-label" htmlFor="software-cost">
              Software and tools
            </label>
            <input
              id="software-cost"
              disabled={!isWorkspaceAdmin}
              placeholder="49.00"
              value={softwareCost}
              onChange={(event) => setSoftwareCost(event.target.value)}
            />

            <label className="field-label" htmlFor="other-cost">
              Other costs
            </label>
            <input
              id="other-cost"
              disabled={!isWorkspaceAdmin}
              placeholder="0.00"
              value={otherCost}
              onChange={(event) => setOtherCost(event.target.value)}
            />

            <button disabled={savingExpenses || !isWorkspaceAdmin} onClick={saveExpenses} type="button">
              {savingExpenses ? "Saving..." : "Save Expenses"}
            </button>
            <p className="muted-text">
              {isWorkspaceAdmin
                ? "These costs are shared across the workspace and used for net profit estimates."
                : "Only workspace owners or admins can update the shared expense values."}
            </p>
          </article>

          <article className="card finance-card">
            <span className="eyebrow">Estimated Revenue</span>
            <strong>{currencyFormatter.format(filteredTotals.revenue)}</strong>
            <p className="muted-text">Based on conversions multiplied by each content link&apos;s payout value for the current {selectedLabel}.</p>
          </article>

          <article className="card finance-card">
            <span className="eyebrow">{analysisFilter === "workspace" ? "Monthly Expenses" : "Allocated Expenses"}</span>
            <strong>{currencyFormatter.format(filteredExpenses)}</strong>
            <p className="muted-text">
              {analysisFilter === "workspace"
                ? "Ad spend, software, and other workspace costs combined."
                : "Shared workspace expenses allocated to this member based on their revenue share."}
            </p>
          </article>

          <article className="card finance-card">
            <span className="eyebrow">{analysisFilter === "workspace" ? "Workspace Net Profit" : "Member Estimated Profit"}</span>
            <strong className={filteredNetProfit < 0 ? "negative-value" : ""}>
              {currencyFormatter.format(filteredNetProfit)}
            </strong>
            <p className="muted-text">Estimated revenue minus the shared expenses attached to the current {selectedLabel}.</p>
          </article>

          <article className="card finance-card">
            <span className="eyebrow">Profit Margin</span>
            <strong className={profitMargin < 0 ? "negative-value" : ""}>{decimalFormatter.format(profitMargin)}%</strong>
            <p className="muted-text">Net profit divided by estimated revenue for the current {selectedLabel}.</p>
          </article>

          <article className="card finance-card">
            <span className="eyebrow">Conversion Rate</span>
            <strong>{decimalFormatter.format(conversionRate)}%</strong>
            <p className="muted-text">How efficiently the current {selectedLabel} turns clicks into recorded conversions.</p>
          </article>

          <article className="card finance-card">
            <span className="eyebrow">Earnings Per Click</span>
            <strong>{currencyFormatter.format(earningsPerClick)}</strong>
            <p className="muted-text">Revenue per click across the current {selectedLabel}.</p>
          </article>
        </section>
          </div>
        </details>

        {celebrationItems.length ? (
          <section className="dashboard-grid celebration-grid">
            {celebrationItems.map((celebration) => (
              <article className="card celebration-card" key={celebration.key}>
                <span className="eyebrow">Milestone Reached</span>
                <h2>{celebration.title}</h2>
                <p>{celebration.text}</p>
              </article>
            ))}
          </section>
        ) : null}

        <details className="dashboard-details advanced-dashboard-details" {...desktopOpenDetails}>
          <summary>
            <span>Goals and charts</span>
            <small>Milestones, trends</small>
          </summary>
          <div className="dashboard-details-body">
        <section className="card">
          <div className="section-heading">
            <div>
              <span className="eyebrow">Milestone Tracker</span>
              <h2>{analysisFilter === "workspace" ? "Growth milestones" : `${selectedMemberMetric?.label || "Member"} milestones`}</h2>
            </div>
            <p className="muted-text">Track momentum across revenue, conversions, and click volume for the current {selectedLabel}.</p>
          </div>

          <section className="dashboard-grid metrics-grid">
            <article className="card finance-card expense-card">
              <span className="eyebrow">{analysisFilter === "workspace" ? "Workspace Goals" : "Member Goals"}</span>
              <label className="field-label" htmlFor="goal-revenue">
                Revenue goal
              </label>
              <input
                id="goal-revenue"
                disabled={!canEditSelectedGoals}
                value={goalForm.revenue}
                onChange={(event) => setGoalForm((current) => ({ ...current, revenue: event.target.value }))}
                placeholder="1000"
              />

              <label className="field-label" htmlFor="goal-conversions">
                Conversion goal
              </label>
              <input
                id="goal-conversions"
                disabled={!canEditSelectedGoals}
                value={goalForm.conversions}
                onChange={(event) => setGoalForm((current) => ({ ...current, conversions: event.target.value }))}
                placeholder="50"
              />

              <label className="field-label" htmlFor="goal-clicks">
                Click goal
              </label>
              <input
                id="goal-clicks"
                disabled={!canEditSelectedGoals}
                value={goalForm.clicks}
                onChange={(event) => setGoalForm((current) => ({ ...current, clicks: event.target.value }))}
                placeholder="1000"
              />

              <button disabled={savingGoals || !canEditSelectedGoals} onClick={saveGoals} type="button">
                {savingGoals ? "Saving..." : "Save Goals"}
              </button>
              <p className="muted-text">
                {canEditSelectedGoals
                  ? `These custom goals shape the progress bars for the current ${selectedLabel}.`
                  : "Only workspace admins or the selected member can update these goals."}
              </p>
            </article>

            <article className="card finance-card">
              <span className="eyebrow">Revenue Goal Progress</span>
              <strong>{goalSummary.revenue.currentLabel}</strong>
              <div className="milestone-progress-track" aria-hidden="true">
                <span className="milestone-progress-fill" style={{ width: `${goalSummary.revenue.progressPercent}%` }} />
              </div>
              <p className="muted-text">Target: {goalSummary.revenue.targetLabel}</p>
            </article>

            <article className="card finance-card">
              <span className="eyebrow">Conversion Goal Progress</span>
              <strong>{goalSummary.conversions.currentLabel}</strong>
              <div className="milestone-progress-track" aria-hidden="true">
                <span className="milestone-progress-fill" style={{ width: `${goalSummary.conversions.progressPercent}%` }} />
              </div>
              <p className="muted-text">Target: {goalSummary.conversions.targetLabel}</p>
            </article>

            <article className="card finance-card">
              <span className="eyebrow">Traffic Goal Progress</span>
              <strong>{goalSummary.clicks.currentLabel}</strong>
              <div className="milestone-progress-track" aria-hidden="true">
                <span className="milestone-progress-fill" style={{ width: `${goalSummary.clicks.progressPercent}%` }} />
              </div>
              <p className="muted-text">Target: {goalSummary.clicks.targetLabel}</p>
            </article>
          </section>

          <div className="milestone-grid">
            {[
              {
                key: "revenue",
                title: "Revenue Goal",
                helper: "Estimated revenue milestones for affiliate growth."
              },
              {
                key: "conversions",
                title: "Conversion Goal",
                helper: "Celebrate the moments when conversions start compounding."
              },
              {
                key: "clicks",
                title: "Traffic Goal",
                helper: "Keep a simple pulse on audience and campaign momentum."
              }
            ].map((item) => {
              const milestone = milestoneSummary[item.key];

              return (
                <article className="card milestone-card" key={item.key}>
                  <span className="eyebrow">{item.title}</span>
                  <strong>{milestone.currentLabel}</strong>
                  <p className="muted-text">{item.helper}</p>

                  <div className="milestone-progress-track" aria-hidden="true">
                    <span className="milestone-progress-fill" style={{ width: `${milestone.progressPercent}%` }} />
                  </div>

                  <div className="milestone-meta">
                    <span>Progress: {decimalFormatter.format(milestone.progressPercent)}%</span>
                    <span>Next: {milestone.nextLabel}</span>
                  </div>

                  <div className="milestone-badge-row">
                    {milestone.completed.length ? (
                      milestone.completed.map((threshold) => (
                        <span className="milestone-badge" key={threshold}>
                          Hit {item.key === "revenue" ? currencyFormatter.format(threshold) : threshold.toLocaleString("en-US")}
                        </span>
                      ))
                    ) : (
                      <span className="milestone-badge milestone-badge-muted">First milestone in progress</span>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
        </section>

        <section className="dashboard-grid chart-grid">
          <section className="card chart-card">
            <h2>Traffic and conversion trend</h2>
            {performanceLabels.length ? (
              <Chart labels={performanceLabels} datasets={performanceDatasets} />
            ) : (
              <p>No click data yet for this view. Create your first content link to get started.</p>
            )}
          </section>

          <section className="card chart-card">
            <h2>Profit trend by content</h2>
            {performanceLabels.length ? (
              <Chart
                datasets={financialDatasets}
                labels={performanceLabels}
                options={{
                  plugins: {
                    legend: {
                      position: "top"
                    }
                  }
                }}
              />
            ) : (
              <p>Add payout values and workspace expenses to start tracking estimated profit.</p>
            )}
          </section>
        </section>

        {isTeamWorkspace && memberMetrics.length ? (
          <section className="dashboard-grid chart-grid">
            <section className="card chart-card">
              <h2>Team leaderboard by traffic</h2>
              <Chart
                datasets={leaderboardTrafficDatasets}
                labels={leaderboardLabels}
                type="bar"
                options={{
                  plugins: {
                    legend: {
                      position: "top"
                    }
                  }
                }}
              />
            </section>

            <section className="card chart-card">
              <h2>Team leaderboard by revenue and profit</h2>
              <Chart
                datasets={leaderboardFinancialDatasets}
                labels={leaderboardLabels}
                type="bar"
                options={{
                  plugins: {
                    legend: {
                      position: "top"
                    }
                  }
                }}
              />
            </section>
          </section>
        ) : null}
          </div>
        </details>
        </>
        ) : null}

        <section className="card table-card">
          <h2>{analysisFilter === "workspace" ? "Workspace content links" : `${selectedMemberMetric?.label || "Member"} content links`}</h2>
          <p className="muted-text">
            Edit active content links, archive the ones you no longer use, and keep historical performance without spending an active slot.
          </p>
          {filteredLinks.length ? (
            <div className="program-section-stack">
              <div className="program-section-heading">
                <div>
                  <span className="eyebrow">Active Content Links</span>
                  <h3>Currently tracking revenue</h3>
                </div>
                <span className="mobile-chip">{activeFilteredLinks.length} active</span>
              </div>
              {activeFilteredLinks.length ? (
                <div className="link-list">
                  {activeFilteredLinks.map((link) => renderLinkCard(link))}
                </div>
              ) : (
                <p className="muted-text">No active content links in this view right now.</p>
              )}

              {archivedFilteredLinks.length ? (
                <>
                  <div className="program-section-heading">
                    <div>
                      <span className="eyebrow">Removed Content Links</span>
                      <h3>Inactive but saved</h3>
                    </div>
                    <span className="mobile-chip muted-chip">{archivedFilteredLinks.length} removed</span>
                  </div>
                  <div className="link-list">
                    {archivedFilteredLinks.map((link) => renderLinkCard(link))}
                  </div>
                </>
              ) : null}
            </div>
          ) : (
            <div className="empty-state">
              <h3>No content links yet</h3>
              <p>Create your first content link to track clicks, conversions, EPC, and revenue by platform.</p>
            </div>
          )}
        </section>
      </div>
    </ProductShell>
  );
}
